import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiAlertCircle,
  FiArrowLeft,
  FiArrowRight,
  FiBriefcase,
  FiCheck,
  FiClock,
  FiCreditCard,
  FiPackage,
  FiRefreshCw,
  FiRotateCcw,
  FiX,
} from 'react-icons/fi';
import { api } from '../services/api';
import { useLocale } from '../context/LocaleContext';
import {
  formatTransactionAmount,
  formatTransactionDate,
  transactionModeLabelLocalized,
  transactionStatusInfoLocalized,
} from '../utils/paymentTransactions';
import './UserPayments.css';

const STATUS_FILTERS = [
  'all', 'payable', 'succeeded', 'failed', 'pending', 'expired', 'refunded',
];

const TYPE_FILTERS = ['all', 'service', 'product'];

const statusIcon = (status) => {
  if (status === 'succeeded') return <FiCheck />;
  if (status === 'failed') return <FiX />;
  if (status === 'refunded') return <FiRotateCcw />;
  return <FiClock />;
};

const UserPayments = ({ onOpenServiceOrder, onOpenProductOrders }) => {
  const { t } = useTranslation();
  const { locale, formatNumber } = useLocale();
  const numberLocale = locale.intlLocale || locale.code;
  const [transactions, setTransactions] = useState([]);
  const [outstandingPayments, setOutstandingPayments] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    succeeded: 0,
    failed: 0,
    pending: 0,
    paidAmount: 0,
    payable: 0,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    pageCount: 1,
    total: 0,
  });
  const [status, setStatus] = useState('all');
  const [type, setType] = useState('all');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [payingOrderId, setPayingOrderId] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    const params = new URLSearchParams({
      page: String(page),
      limit: '10',
      status,
      type,
    });

    api.get(`/api/payment/my-transactions?${params.toString()}`)
      .then((response) => {
        if (!active) return;
        setTransactions(Array.isArray(response.data) ? response.data : []);
        setOutstandingPayments(
          Array.isArray(response.outstandingPayments) ? response.outstandingPayments : []
        );
        setSummary(response.summary || {});
        setPagination(response.pagination || { page: 1, pageCount: 1, total: 0 });
      })
      .catch((requestError) => {
        if (!active) return;
        setError(requestError.data?.message || requestError.message || t('customerUi.payments.loadError'));
        setTransactions([]);
        setOutstandingPayments([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [page, refreshKey, status, t, type]);

  const updateStatus = (nextStatus) => {
    setStatus(nextStatus);
    setPage(1);
  };

  const updateType = (nextType) => {
    setType(nextType);
    setPage(1);
  };

  const openTransactionOrder = (transaction) => {
    if (transaction.orderType === 'service' && transaction.orderId) {
      onOpenServiceOrder?.(transaction.orderId);
      return;
    }
    if (transaction.orderType === 'product') onOpenProductOrders?.();
  };

  const openPayablePayment = async (payment) => {
    if (!payment?.orderId) return;
    if (payment.orderType === 'service') {
      onOpenServiceOrder?.(payment.orderId);
      return;
    }
    if (!payment.canPay) return;
    setPayingOrderId(payment.orderId);
    setError('');
    try {
      const response = await api.post(`/api/orders/${payment.orderId}/payment-request`, {});
      if (!response.data?.paymentUrl) throw new Error(t('customerUi.payments.gatewayMissing'));
      window.location.assign(response.data.paymentUrl);
    } catch (paymentError) {
      setError(
        paymentError.data?.message
        || paymentError.message
        || t('customerUi.payments.gatewayError')
      );
      setPayingOrderId('');
    }
  };

  return (
    <section className="user-payments user-panel-section-card">
      <header className="user-payments-heading">
        <div>
          <span><FiCreditCard /></span>
          <div>
            <h3>{t('customerUi.payments.title')}</h3>
            <p>{t('customerUi.payments.subtitle')}</p>
          </div>
        </div>
        <button type="button" onClick={() => setRefreshKey((value) => value + 1)} disabled={loading}>
          <FiRefreshCw /> {loading ? t('customerUi.common.loading') : t('customerUi.common.refresh')}
        </button>
      </header>

      <div className="user-payments-summary">
        <article className="is-total">
          <span><FiCreditCard /></span>
          <div><small>{t('customerUi.payments.total')}</small><strong>{formatNumber(summary.total)}</strong></div>
        </article>
        <article className="is-success">
          <span><FiCheck /></span>
          <div><small>{t('customerUi.payments.successful')}</small><strong>{formatNumber(summary.succeeded)}</strong></div>
        </article>
        <article className="is-failed">
          <span><FiX /></span>
          <div><small>{t('customerUi.payments.failedExpired')}</small><strong>{formatNumber(summary.failed)}</strong></div>
        </article>
        <article className="is-amount">
          <span><FiCreditCard /></span>
          <div>
            <small>{t('customerUi.payments.paidTotal')}</small>
            {Array.isArray(summary.paidByCurrency) && summary.paidByCurrency.length
              ? summary.paidByCurrency.map((item) => (
                <strong key={item.currencyCode || item.currency}>
                  {formatTransactionAmount(item.amount, numberLocale)} <em>{item.currency}</em>
                </strong>
              ))
              : <strong>{formatTransactionAmount(summary.paidAmount, numberLocale)} <em>{t('customerUi.common.toman')}</em></strong>}
          </div>
        </article>
      </div>

      <div className="user-payments-toolbar">
        <div className="user-payments-filter" aria-label={t('customerUi.payments.statusFilter')}>
          {STATUS_FILTERS.map((id) => (
            <button
              type="button"
              className={status === id ? 'active' : ''}
              onClick={() => updateStatus(id)}
              key={id}
            >
              {t(`customerUi.payments.statuses.${id}`)}
            </button>
          ))}
        </div>
        <select value={type} onChange={(event) => updateType(event.target.value)} aria-label={t('customerUi.payments.paymentType')}>
          {TYPE_FILTERS.map((id) => <option value={id} key={id}>{t(`customerUi.payments.types.${id}`)}</option>)}
        </select>
      </div>

      {error && (
        <div className="user-payments-error" role="alert">
          <FiAlertCircle />
          <span>{error}</span>
          <button type="button" onClick={() => setRefreshKey((value) => value + 1)}>{t('customerUi.common.retry')}</button>
        </div>
      )}

      {!loading && outstandingPayments.length > 0 && (
        <section className="user-payments-payable">
          <header>
            <div>
              <span><FiCreditCard /></span>
              <div>
                <strong>{t('customerUi.payments.payableTitle')}</strong>
                <small>{t('customerUi.payments.unpaidCount', { count: formatNumber(outstandingPayments.length) })}</small>
              </div>
            </div>
          </header>
          <div>
            {outstandingPayments.map((payment) => {
              const isServicePayment = payment.orderType === 'service';
              return (
                <article key={payment.id} className={isServicePayment ? 'is-service-payment' : ''}>
                  <span>{isServicePayment ? <FiBriefcase /> : <FiPackage />}</span>
                  <div>
                    <strong>{payment.title || t(isServicePayment ? 'customerUi.payments.serviceOrder' : 'customerUi.payments.productPurchase')}</strong>
                    <small>
                      {t(isServicePayment ? 'customerUi.payments.service' : 'customerUi.payments.product')}
                      {payment.orderNumber && ` · ${t('customerUi.userPanel.order', { id: payment.orderNumber })}`}
                      {' · '}{formatTransactionDate(payment.createdAt, numberLocale)}
                    </small>
                    <p>
                      {payment.paidAmount > 0
                        ? t('customerUi.payments.paidBalance', {
                          amount: formatTransactionAmount(payment.paidAmount, numberLocale),
                          currency: payment.currency || t('customerUi.common.toman'),
                        })
                        : isServicePayment
                          ? t('customerUi.payments.serviceUnpaid')
                          : t('customerUi.payments.unpaid')}
                    </p>
                  </div>
                  <div>
                    <strong>{formatTransactionAmount(payment.amount, numberLocale)}</strong>
                    <small>{payment.currency || t('customerUi.common.toman')}</small>
                    <button
                      type="button"
                      onClick={() => openPayablePayment(payment)}
                      disabled={!isServicePayment && (!payment.canPay || payingOrderId === payment.orderId)}
                    >
                      <FiCreditCard />
                      {isServicePayment
                        ? payment.status === 'processing'
                          ? t('customerUi.payments.viewPendingService')
                          : payment.paidAmount > 0
                            ? t('customerUi.payments.continueServicePayment')
                            : t('customerUi.payments.payService')
                        : payingOrderId === payment.orderId
                          ? t('customerUi.payments.connecting')
                          : payment.canPay
                            ? payment.paidAmount > 0 ? t('customerUi.payments.payBalance') : t('customerUi.payments.payOrder')
                            : payment.gatewayAvailable
                              ? t('customerUi.payments.awaitingBank')
                              : t('customerUi.payments.gatewayUnavailable')}
                    </button>
                    {!isServicePayment && !payment.canPay && (
                      <em>{t(payment.gatewayAvailable
                        ? 'customerUi.payments.parallelHint'
                        : 'customerUi.payments.gatewayUnavailableHint')}</em>
                    )}
                    {isServicePayment && payment.status === 'processing' && (
                      <em>{t('customerUi.payments.servicePendingHint')}</em>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      )}

      {loading ? (
        <div className="user-panel-loading user-panel-loading--large"><span /> {t('customerUi.payments.loadingTransactions')}</div>
      ) : !transactions.length && !outstandingPayments.length ? (
        <div className="user-panel-empty">
          <span><FiCreditCard /></span>
          <div>
            <strong>{t('customerUi.payments.empty')}</strong>
            <p>{t('customerUi.payments.emptyHint')}</p>
          </div>
        </div>
      ) : transactions.length > 0 ? (
        <div className="user-payments-list">
          {transactions.map((transaction) => {
            const statusInfo = transactionStatusInfoLocalized(transaction.status, t);
            return (
              <article key={transaction.id}>
                <span className={`user-payments-status-icon is-${statusInfo.tone}`}>
                  {statusIcon(transaction.status)}
                </span>
                <div className="user-payments-main">
                  <div>
                    <strong>{transaction.title || (transaction.orderType === 'service' ? t('customerUi.payments.serviceOrder') : t('customerUi.payments.productPurchase'))}</strong>
                    <span className={`user-payments-status is-${statusInfo.tone}`}>{statusInfo.label}</span>
                  </div>
                  <p>
                    {transaction.orderType === 'service' ? <FiBriefcase /> : <FiPackage />}
                    {transaction.orderType === 'service' ? t('customerUi.payments.service') : t('customerUi.payments.product')}
                    {' · '}{transactionModeLabelLocalized(transaction, t)}
                    {transaction.orderNumber && ` · #${transaction.orderNumber}`}
                  </p>
                  <small>
                    {formatTransactionDate(transaction.completedAt || transaction.requestedAt, numberLocale)}
                    {transaction.source === 'manual'
                      ? ` · ${t('customerUi.payments.manual')}`
                      : transaction.gatewayInvoiceId && ` · ${t('customerUi.payments.identifier', { id: String(transaction.gatewayInvoiceId) })}`}
                    {(transaction.manualReference || transaction.gatewayReference)
                      && ` · ${t('customerUi.payments.tracking', { id: transaction.manualReference || transaction.gatewayReference })}`}
                  </small>
                  {transaction.note && <em className="is-note">{transaction.note}</em>}
                  {transaction.failureMessage && (
                    <em><FiAlertCircle /> {transaction.failureMessage}</em>
                  )}
                </div>
                <div className="user-payments-amount">
                  <strong>{formatTransactionAmount(transaction.amount, numberLocale)}</strong>
                  <span>{transaction.currency || t('customerUi.common.toman')}</span>
                  <button type="button" onClick={() => openTransactionOrder(transaction)}>
                    {t('customerUi.payments.viewOrder')} <FiArrowLeft />
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      ) : null}

      {pagination.pageCount > 1 && (
        <nav className="user-payments-pagination" aria-label={t('customerUi.payments.pagination')}>
          <button type="button" onClick={() => setPage((value) => Math.max(1, value - 1))} disabled={pagination.page <= 1}>
            <FiArrowRight /> {t('customerUi.payments.newer')}
          </button>
          <span>
            {t('customerUi.common.page', {
              page: formatNumber(pagination.page || 1),
              pages: formatNumber(pagination.pageCount || 1),
            })}
          </span>
          <button type="button" onClick={() => setPage((value) => Math.min(pagination.pageCount, value + 1))} disabled={pagination.page >= pagination.pageCount}>
            {t('customerUi.payments.older')} <FiArrowLeft />
          </button>
        </nav>
      )}
    </section>
  );
};

export default UserPayments;
